import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { SHEET_CLOSE_MS } from "./sheetConstants";
import "./BottomSheet.css";

export default function BottomSheet({
  open,
  onClose,
  title,
  children,
  footer,
  className = "",
}) {
  const [shouldRender, setShouldRender] = useState(open);
  const [isAnimatedIn, setIsAnimatedIn] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const onCloseRef = useRef(onClose);
  const isClosingRef = useRef(false);
  onCloseRef.current = onClose;

  const finishClose = useCallback(() => {
    isClosingRef.current = false;
    setIsClosing(false);
    setShouldRender(false);
    setIsAnimatedIn(false);
    onCloseRef.current?.();
  }, []);

  const requestClose = useCallback(() => {
    if (isClosingRef.current) return;
    isClosingRef.current = true;
    setIsClosing(true);
    setIsAnimatedIn(false);
    window.setTimeout(finishClose, SHEET_CLOSE_MS);
  }, [finishClose]);

  useEffect(() => {
    if (!open) return undefined;

    setShouldRender(true);
    isClosingRef.current = false;
    setIsClosing(false);

    const enterFrame = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        setIsAnimatedIn(true);
      });
    });

    return () => {
      window.cancelAnimationFrame(enterFrame);
    };
  }, [open]);

  useEffect(() => {
    if (open || !shouldRender) return undefined;
    if (!isClosingRef.current) {
      requestClose();
    }
    return undefined;
  }, [open, shouldRender, requestClose]);

  useEffect(() => {
    if (!shouldRender) return undefined;

    const onKey = (event) => {
      if (event.key === "Escape") requestClose();
    };
    window.addEventListener("keydown", onKey);

    return () => {
      window.removeEventListener("keydown", onKey);
    };
  }, [shouldRender, requestClose]);

  if (!shouldRender) return null;

  const rootClass = [
    "ui-bottom-sheet-root",
    isAnimatedIn ? "ui-bottom-sheet-root--open" : "",
    isClosing ? "ui-bottom-sheet-root--closing" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return createPortal(
    <div className={rootClass} role="presentation">
      <button
        type="button"
        className="ui-bottom-sheet-backdrop"
        aria-label="Close"
        onClick={requestClose}
      />
      <div
        className="ui-bottom-sheet"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="ui-bottom-sheet__handle" aria-hidden />
        {title ? <h2 className="ui-bottom-sheet__title">{title}</h2> : null}
        <div className="ui-bottom-sheet__body">{children}</div>
        {footer ? <footer className="ui-bottom-sheet__footer">{footer}</footer> : null}
      </div>
    </div>,
    document.body,
  );
}
